<script setup lang="ts">
import { DialogRoot } from 'radix-vue';
import { X } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { DialogTrigger, DialogContent, DialogClose } from '.';
import { Button } from '@/components/ui/button';

interface Props {
    modelValue?: boolean;
    disabled?: boolean;
    closable?: boolean;
}

defineOptions({
    inheritAttrs: false,
});
const props = withDefaults(defineProps<Props>(), {
    modelValue: undefined,
    closable: true,
});
const emit = defineEmits<{
    'update:modelValue': [value: boolean];
}>();

const internalModelValue = ref(false);
const computedModelValue = computed(() => props.modelValue ?? internalModelValue.value);

function handleOpen(value: boolean) {
    if (props.disabled) {
        return;
    }

    if (props.modelValue === undefined) {
        internalModelValue.value = value;
    }

    emit('update:modelValue', value);
}
</script>

<template>
    <DialogRoot :open="computedModelValue" @update:open="handleOpen">
        <DialogTrigger v-if="$slots.trigger" as-child>
            <slot name="trigger" />
        </DialogTrigger>
        <!-- No DialogPortal here: DialogContent already portals itself, and radix requires the overlay
             and the content to live inside the *same* portal. Wrapping it in a second portal teleported
             the same subtree to body twice over, which bought nothing and made the teleport bookkeeping
             harder to reason about than it needs to be. -->
        <DialogContent v-bind="$attrs">
            <slot />
            <DialogClose v-if="closable" as-child>
                <Button :disabled="props.disabled" variant="ghost" size="icon" class="h-8 w-8 absolute top-3 right-3">
                    <span class="sr-only">Close</span>
                    <X class="size-6" aria-hidden="true" />
                </Button>
            </DialogClose>
        </DialogContent>
    </DialogRoot>
</template>
